import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

interface Organization {
  _id: string;
  name: string;
  country: string;
  isActive: boolean;
}

const SelectOrganizationPage: React.FC = () => {
  const { user, setActiveOrganization, needsOrganizationSelection } = useAuth();
  const navigate = useNavigate();
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingOrg, setIsSettingOrg] = useState(false);

  // Fetch organization details for user's organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      console.log('SelectOrganizationPage - user:', user);
      console.log('SelectOrganizationPage - user organizations:', user?.organizations);
      
      if (!user?.organizations || user.organizations.length === 0) {
        console.log('SelectOrganizationPage - no organizations found');
        setIsLoading(false);
        return;
      }

      try {
        const orgIds = user.organizations
          .map(org => org.organizationId)
          .filter(id => id && id.trim && id.trim() !== '');
        console.log('SelectOrganizationPage - fetching orgs by ids:', orgIds);
        
        if (orgIds.length === 0) {
          console.log('SelectOrganizationPage - no valid org IDs');
          setIsLoading(false);
          return;
        }
        
        const response = await api.post('/organizations/by-ids', { ids: orgIds });
        console.log('SelectOrganizationPage - response:', response.data);
        
        if (response.data?.success) {
          const fetchedOrgs = response.data.data || [];
          setOrganizations(fetchedOrgs);
          
          // Auto-select if only one organization
          if (fetchedOrgs.length === 1) {
            setSelectedOrgId(fetchedOrgs[0]._id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        toast.error('Failed to load organizations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, [user?.organizations]);

  // If user doesn't need organization selection, redirect them
  useEffect(() => {
    if (!needsOrganizationSelection && !isLoading) {
      navigate('/app', { replace: true });
    }
  }, [needsOrganizationSelection, isLoading, navigate]);

  const handleSelectOrganization = async () => {
    if (!selectedOrgId) {
      toast.error('Please select an organization');
      return;
    }

    setIsSettingOrg(true);
    try {
      const selectedOrg = user?.organizations?.find(org => org.organizationId === selectedOrgId);
      if (selectedOrg) {
        await setActiveOrganization(selectedOrg);
        toast.success('Organization selected successfully');
        navigate('/app', { replace: true });
      }
    } catch (error) {
      console.error('Failed to set organization:', error);
    } finally {
      setIsSettingOrg(false);
    }
  };

  const handleSkipForNow = async () => {
    // Set first organization as active temporarily
    if (user?.organizations && user.organizations.length > 0) {
      try {
        await setActiveOrganization(user.organizations[0]);
        navigate('/app', { replace: true });
      } catch (error) {
        console.error('Failed to skip organization selection:', error);
      }
    } else {
      navigate('/onboarding', { replace: true });
    }
  };

  const handleCreateNewOrganization = () => {
    navigate('/onboarding?mode=create', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <BuildingOfficeIcon className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          {organizations.length === 1 ? 'Continue to Organization' : 'Select Organization'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          {organizations.length === 1 
            ? 'You have access to one organization. Continue to proceed.'
            : 'You have access to multiple organizations. Please select one to continue.'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-4">
            {organizations.map((org) => {
              const userOrg = user?.organizations?.find(uo => uo.organizationId === org._id);
              return (
                <div
                  key={org._id}
                  onClick={() => setSelectedOrgId(org._id)}
                  className={`
                    relative rounded-lg border p-4 cursor-pointer transition-all
                    ${selectedOrgId === org._id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                        {org.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {org.country} • {userOrg?.role}
                      </p>
                    </div>
                    {selectedOrgId === org._id && (
                      <div className="flex-shrink-0">
                        <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 space-y-3">
            <button
              onClick={handleSelectOrganization}
              disabled={!selectedOrgId || isSettingOrg}
              className="flex w-full justify-center items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSettingOrg ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <ArrowRightIcon className="h-4 w-4 mr-2" />
              )}
              {organizations.length === 1 && selectedOrgId
                ? `Continue to ${organizations.find(o => o._id === selectedOrgId)?.name || 'Organization'}`
                : 'Continue'}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleSkipForNow}
                className="flex justify-center items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Skip for Now
              </button>
              
              <button
                onClick={handleCreateNewOrganization}
                className="flex justify-center items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Create New
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              You can change organizations later from your profile settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectOrganizationPage;
